type SidebarIconName =
  | 'agenda'
  | 'calendar'
  | 'calendar-range'
  | 'chat'
  | 'clock'
  | 'dashboard'
  | 'document'
  | 'logout'
  | 'phone'
  | 'profile'
  | 'settings'
  | 'spark'
  | 'surgery-after'
  | 'surgery-before'
  | 'surgery-call'
  | 'surgery-cooling'
  | 'surgery-cup'
  | 'surgery-document'
  | 'surgery-medicine'
  | 'users';

type SidebarIconProps = {
  name?: SidebarIconName;
  className?: string;
};

const iconClassName = 'h-4 w-4';

const DashboardIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="currentColor" aria-hidden="true">
    <rect x="3" y="3" width="6" height="6" rx="1.2" />
    <rect x="11" y="3" width="6" height="6" rx="1.2" />
    <rect x="3" y="11" width="6" height="6" rx="1.2" />
    <rect x="11" y="11" width="6" height="6" rx="1.2" />
  </svg>
);

const CalendarIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M4.5 6.5h11" strokeLinecap="round" />
    <path d="M6 3.5v3M14 3.5v3" strokeLinecap="round" />
    <rect x="3.5" y="4.5" width="13" height="12" rx="2.2" />
  </svg>
);

const ClockIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <circle cx="10" cy="10" r="6.5" />
    <path d="M10 6.5V10l2.5 1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CalendarRangeIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M4.5 6.5h11" strokeLinecap="round" />
    <path d="M6 3.5v3M14 3.5v3" strokeLinecap="round" />
    <rect x="3.5" y="4.5" width="13" height="12" rx="2.2" />
    <path d="M7 10h6M7 13h4" strokeLinecap="round" />
  </svg>
);

const ProfileIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <circle cx="10" cy="7" r="2.6" />
    <path d="M5.5 15c.7-2.1 2.5-3.3 4.5-3.3s3.8 1.2 4.5 3.3" strokeLinecap="round" />
  </svg>
);

const PhoneIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path
      d="M6.4 4.7c.3-.5.9-.8 1.4-.6l1.6.6c.5.2.8.7.7 1.2l-.3 1.6c0 .4.1.7.3 1a9 9 0 0 0 2.3 2.3c.3.2.7.3 1 .2l1.5-.3c.6-.1 1.1.2 1.3.7l.5 1.6c.2.6 0 1.1-.6 1.4l-1.1.6c-.7.4-1.6.4-2.3.1A12.8 12.8 0 0 1 4.8 8c-.3-.7-.2-1.5.2-2.2l1.4-1.1Z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChatIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M5.5 6.5h9a2 2 0 0 1 2 2v3.5a2 2 0 0 1-2 2H10l-3.5 2v-2H5.5a2 2 0 0 1-2-2V8.5a2 2 0 0 1 2-2Z" strokeLinejoin="round" />
    <path d="M7.5 10h5" strokeLinecap="round" />
  </svg>
);

const DocumentIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M7 3.5h4.9l2.6 2.6v8.4A1.5 1.5 0 0 1 13 16H7a1.5 1.5 0 0 1-1.5-1.5V5A1.5 1.5 0 0 1 7 3.5Z" strokeLinejoin="round" />
    <path d="M11.8 3.8V6.4H14.4" strokeLinejoin="round" />
    <path d="M8 9.2h4M8 11.5h4" strokeLinecap="round" />
  </svg>
);

const LogoutIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M8 4.5H5.8a1.8 1.8 0 0 0-1.8 1.8v7.4a1.8 1.8 0 0 0 1.8 1.8H8" strokeLinecap="round" />
    <path d="M10.5 10h5" strokeLinecap="round" />
    <path d="M13 7.5 15.5 10 13 12.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SettingsIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <circle cx="10" cy="10" r="2.3" />
    <path
      d="M10 3.5v1.6M10 14.9v1.6M14.6 5.4l-1.1 1.1M6.5 13.5l-1.1 1.1M16.5 10h-1.6M5.1 10H3.5M14.6 14.6l-1.1-1.1M6.5 6.5 5.4 5.4"
      strokeLinecap="round"
    />
  </svg>
);

const UsersIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <circle cx="7.2" cy="7.5" r="2.2" />
    <circle cx="13.3" cy="8.2" r="1.8" />
    <path d="M3.8 14.7c.6-1.9 2.1-3 3.9-3s3.3 1.1 3.9 3" strokeLinecap="round" />
    <path d="M11.5 14.5c.4-1.4 1.5-2.2 2.9-2.2 1 0 1.9.4 2.5 1.2" strokeLinecap="round" />
  </svg>
);

const SparkIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M10 3.5 11.4 7 15 8.4 11.4 9.8 10 13.3 8.6 9.8 5 8.4 8.6 7 10 3.5Z" strokeLinejoin="round" />
  </svg>
);

const AgendaIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M6 4v3M14 4v3" strokeLinecap="round" />
    <rect x="3.5" y="5.5" width="13" height="11" rx="2.2" />
    <path d="M6.5 9h7M6.5 12h5" strokeLinecap="round" />
  </svg>
);

const SurgeryDocumentIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M6.3 3.8h4.7l2.8 2.8v7.9a1.7 1.7 0 0 1-1.7 1.7H6.3a1.7 1.7 0 0 1-1.7-1.7V5.5a1.7 1.7 0 0 1 1.7-1.7Z" strokeLinejoin="round" />
    <path d="M11 3.8v2.9h2.8" strokeLinejoin="round" />
    <path d="M7.6 9.2h4.8M7.6 11.8h4.8" strokeLinecap="round" />
  </svg>
);

const SurgeryBeforeIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M5.8 3.8v2.4M14.2 3.8v2.4" strokeLinecap="round" />
    <rect x="4.2" y="5.3" width="11.6" height="10.3" rx="2.1" />
    <path d="M4.8 8h10.4" strokeLinecap="round" />
    <path d="M10 10.2v3.4M8.3 11.9H11.7" strokeLinecap="round" />
  </svg>
);

const SurgeryAfterIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M5.8 3.8v2.4M14.2 3.8v2.4" strokeLinecap="round" />
    <rect x="4.2" y="5.3" width="11.6" height="10.3" rx="2.1" />
    <path d="M4.8 8h10.4" strokeLinecap="round" />
    <path d="M8 12l1.5 1.5L12.5 10.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SurgeryCoolingIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M10 3.8v12.4M5.5 6.2l9 7.6M14.5 6.2l-9 7.6" strokeLinecap="round" />
    <circle cx="10" cy="10" r="1.9" />
  </svg>
);

const SurgeryMedicineIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.65" aria-hidden="true">
    <path d="M8 4.2h4M8.7 4.2V6h2.6V4.2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.3 6.1h5.4v8.1a1.9 1.9 0 0 1-1.9 1.9H9.2a1.9 1.9 0 0 1-1.9-1.9z" strokeLinejoin="round" />
    <path d="M10 8.5v4.3M7.9 12.7h4.2" strokeLinecap="round" />
  </svg>
);

const SurgeryCupIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.65" aria-hidden="true">
    <path d="M5.2 6.3h7.5v3.5a3.8 3.8 0 0 1-3.8 3.8 3.8 3.8 0 0 1-3.7-3.8z" strokeLinejoin="round" />
    <path d="M12.7 7.1h1.1a1.9 1.9 0 0 1 0 3.8h-1.1" strokeLinecap="round" />
    <path d="M6.7 15.7h4.6" strokeLinecap="round" />
  </svg>
);

const SurgeryCallIcon = ({ className = iconClassName }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="1.65" aria-hidden="true">
    <path d="M6.4 10.3v-1a3.6 3.6 0 0 1 7.2 0v1" strokeLinecap="round" />
    <path d="M5.1 10.2v2.6a1.2 1.2 0 0 0 1.2 1.2h.4v-3.8h-.4a1.2 1.2 0 0 0-1.2 1.2Zm8.2 0v3.8h.4a1.2 1.2 0 0 0 1.2-1.2v-2.6a1.2 1.2 0 0 0-1.2-1.2z" strokeLinejoin="round" />
    <path d="M8 14.8c.4.7 1.1 1.1 2 1.1h1.5" strokeLinecap="round" />
  </svg>
);

const iconMap: Record<SidebarIconName, ({ className }: { className?: string }) => JSX.Element> = {
  agenda: AgendaIcon,
  calendar: CalendarIcon,
  'calendar-range': CalendarRangeIcon,
  chat: ChatIcon,
  clock: ClockIcon,
  dashboard: DashboardIcon,
  document: DocumentIcon,
  logout: LogoutIcon,
  phone: PhoneIcon,
  profile: ProfileIcon,
  settings: SettingsIcon,
  spark: SparkIcon,
  'surgery-after': SurgeryAfterIcon,
  'surgery-before': SurgeryBeforeIcon,
  'surgery-call': SurgeryCallIcon,
  'surgery-cooling': SurgeryCoolingIcon,
  'surgery-cup': SurgeryCupIcon,
  'surgery-document': SurgeryDocumentIcon,
  'surgery-medicine': SurgeryMedicineIcon,
  users: UsersIcon
};

export function SidebarIcon({ name, className }: SidebarIconProps) {
  if (!name) {
    return <span className={className ?? iconClassName} aria-hidden="true" />;
  }

  const IconComponent = iconMap[name];
  return IconComponent ? <IconComponent className={className} /> : <span className={className ?? iconClassName} aria-hidden="true" />;
}

export type { SidebarIconName };
